import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AdminAdminUser as AdminUser, AdminAdminUserCreate as AdminUserCreate, AdminAdminUserUpdate as AdminUserUpdate, TenantsGlobalRole as GlobalRole, TenantsRoomRole as RoomRole } from '../generated/api/types.gen.ts';
import { adminUsersCreate, adminUsersDisable, adminUsersEnable, adminUsersList, adminUsersUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { AdminUser, AdminUserCreate, AdminUserUpdate, GlobalRole, RoomRole };
type Rom = AdminUser['roomAccess'][number]['room'];

/** Nøkkelen eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). */
export const adminUserNoekkel = {
  alle: ['admin-users'] as const,
  liste: ['admin-users', 'liste'] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/**
 * `GET /v1/admin/users` — leietakerens panelbrukere, også de deaktiverte. **Kun eier**, også
 * for å liste: dette er den ene kontraktmodellen som bærer e-post, fordi eieren må vite HVEM
 * hun gir tilgang. `idpSub` går aldri ut.
 *
 * `aktiv` er rollesjekken fra kallstedet. Lista hentes ikke for andre enn eier — en 403 er
 * ikke en tilstand å vise når svaret er kjent på forhånd (samme mønster som `useApiKeys`).
 * Personsiden har ikke eget GET i kontrakten: den plukker raden herfra.
 */
export function useAdminUsers(aktiv = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: adminUserNoekkel.liste,
    enabled: aktiv,
    queryFn: async (): Promise<AdminUser[]> => {
      const res = await adminUsersList({ client: klient });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as AdminUser[];
    },
  });
}

/** Legger serverens svar inn i lista, så siden står på det lagrede uten å hente på nytt. */
function erstatt(queryClient: QueryClient, id: string, endre: (b: AdminUser) => AdminUser) {
  queryClient.setQueryData<AdminUser[]>(adminUserNoekkel.liste, (liste) => liste?.map((b) => (b.id === id ? endre(b) : b)));
}

function useKall(fn: (id: string) => Promise<{ response?: Response; data?: unknown; error?: unknown }>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fn(id);
      if (!res.response?.ok) kast(res);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminUserNoekkel.alle }),
  });
}

/**
 * Deaktiverer kontoen. Brukerens neste admin-kall gir 401 uansett hvor ferskt
 * innloggingstokenet er — sesjonen overlever ikke. Raden står, og alt hun gjorde er fortsatt
 * sporbart (`audit_entry` peker på den med RESTRICT). Idempotent: alt deaktivert gir 204.
 * Egen konto gir 409 `cannot_disable_self`; panelet låser FØR det skjer, men to faner er nok
 * til at svaret må håndteres. Rom-radene røres ikke — en aktivert konto får tilgangen som sto.
 */
export function useDeaktiverAdminUser() {
  const klient = useApiKlient();
  return useKall((id) => adminUsersDisable({ client: klient, path: { id } }));
}

/** Reaktiverer. Idempotent: en aktiv konto gir 204. Uten den var eneste vei tilbake SQL som eier. */
export function useAktiverAdminUser() {
  const klient = useApiKlient();
  return useKall((id) => adminUsersEnable({ client: klient, path: { id } }));
}

/** Rom-rollen per rom i et valg: `null` er «Ingen». */
export type Romvalg = Partial<Record<Rom, RoomRole | null>>;

/** Rom-rollene i et valg, slik kontrakten tar dem: rom med «Ingen» står ikke i lista. */
const romTilgang = (rom: Romvalg): AdminUser['roomAccess'] =>
  (Object.keys(rom) as Rom[]).flatMap((room) => {
    const role = rom[room] ?? null;
    return role === null ? [] : [{ room, role }];
  });

/**
 * Det ENE kallet «Lagre tilgangen» gjør (M4.8 steg 4), som ren funksjon, så det kan leses uten
 * nett. `null` betyr at ingenting er endret, og da gjøres ingen kall.
 *
 * - Til **eier**: `PATCH {role: eier}`. Serveren fjerner rom-radene.
 * - Til eller som **medlem**: `PATCH {role: medlem, roomAccess}` med HELE settet. Rom med «Ingen»
 *   står ikke i lista, og det er slik de tas. Rollen og rommene lagres i én transaksjon, så
 *   personen står aldri med en tilgang midt mellom to kall. Mellomtilstanden fra steg 3, der en
 *   eier som ble medlem uten rom først fikk leser i Innspill (P6), finnes ikke lenger.
 */
export function tilgangsendring(fra: { eier: boolean; rom: Romvalg }, til: { eier: boolean; rom: Romvalg }): AdminUserUpdate | null {
  if (til.eier) return fra.eier ? null : { role: 'eier' };
  const valgt = (r: Rom) => til.rom[r] ?? null;
  const uendret = !fra.eier && (Object.keys(til.rom) as Rom[]).every((r) => valgt(r) === (fra.rom[r] ?? null));
  return uendret ? null : { role: 'medlem', roomAccess: romTilgang(til.rom) };
}

/**
 * «Lagre tilgangen» på personens side: ett `PATCH` med {@link tilgangsendring}. Gjelder fra
 * brukerens neste kall — rollene leses fra databasen, aldri fra tokenet. Svaret er AdminUser og
 * legges i lista, så siden står på det lagrede uten å hente på nytt. Egen konto gir 409
 * `cannot_change_own_role`; panelet låser egen rad før det skjer. Hver endring er én audit-rad
 * på serveren (K7).
 */
export function useLagreTilgang() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, endring }: { id: string; endring: AdminUserUpdate }): Promise<AdminUser> => {
      const res = await adminUsersUpdate({ client: klient, path: { id }, body: endring });
      if (!res.response?.ok || !res.data) kast(res);
      const svar = res.data as AdminUser;
      erstatt(queryClient, id, () => svar);
      return svar;
    },
  });
}

/**
 * `POST /v1/admin/users` — «Legg til person» (sete-først, tech-decisions «Adminbrukere fra
 * panelet», DD-68): adressen, rollen og rommene i ETT kall, ventende til personen logger inn med
 * adressen. Setet finnes aldri med en annen tilgang enn den som ble valgt. Panelet sender ingen
 * e-post; kontoen opprettes av innloggingstjenesten ved første innlogging, gatet av hooken
 * før-bruker-opprettes. 409 `admin_user_email_taken` sier ikke hvor adressen finnes.
 */
export function useLeggTilPerson() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, eier, rom }: { email: string; eier: boolean; rom: Romvalg }): Promise<AdminUser> => {
      const body: AdminUserCreate = eier ? { email, role: 'eier' } : { email, role: 'medlem', roomAccess: romTilgang(rom) };
      const res = await adminUsersCreate({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as AdminUser;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: adminUserNoekkel.alle }),
  });
}
